import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';

// Trigger button for a custom dropdown/listbox (e.g. the Monitoring tab's
// driver/route pickers): optional leading icon, truncating label, chevron.
// The menu itself belongs to the consumer — this atom only owns the button
// and its aria-haspopup/aria-expanded wiring.
const StyledButton = styledComponent('button', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 10px 5px 12px',
  background: theme.colors.bgCard,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.radii.input,
  fontSize: theme.sizes.smallFontSize,
  fontWeight: theme.fontWeights.medium,
  color: theme.colors.textBody,
  cursor: 'pointer',
  transition: theme.transitions.default,

  // The leading-icon variant tightens the left inset and reserves room for
  // the label to truncate.
  '&.ddt-has-icon': {
    paddingLeft: '10px',
    minWidth: '160px',
  },
  '&:hover': {
    background: theme.colors.bgMuted,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.colors.infoBlue,
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },

  '& .ddt-icon': {
    display: 'inline-flex',
    color: theme.colors.textMuted,
  },
  '& .ddt-label': {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .ddt-chevron': {
    color: theme.colors.textMuted,
  },
});

function DropdownTrigger({ label, onClick, isOpen, icon, dataTestId }) {
  return (
    <StyledButton
      type="button"
      className={icon ? 'ddt-has-icon' : ''}
      onClick={onClick}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      data-testid={dataTestId}
    >
      {icon && <span className="ddt-icon">{icon}</span>}
      <span className="ddt-label">{label}</span>
      <svg
        className="ddt-chevron"
        width="10"
        height="10"
        viewBox="0 0 12 12"
        aria-hidden="true"
      >
        <path
          d="M3 5l3 3 3-3"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </StyledButton>
  );
}

DropdownTrigger.propTypes = {
  label: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  icon: PropTypes.node,
  dataTestId: PropTypes.string,
};

DropdownTrigger.defaultProps = {
  isOpen: false,
  icon: null,
  dataTestId: undefined,
};

export default DropdownTrigger;
